/**
 * Surface Host Tool
 *
 * Replaces the monolithic AutoSurfaceTool. Hosts the shared `LineTool` subtool
 * (with a `SurfaceKindAdapter`) for drawing/segmenting surfaces, via the
 * `LineHosting` mixin over `SurfaceToolBase` — so it keeps the surface-specific
 * behavior it inherits (isolation clip plane, camera alignment, tolerance/
 * rotation subscriptions) while the mixin provides subtool input routing.
 *
 * Camera alignment is a Ctrl-gated gesture: it only STARTS while Ctrl is held
 * (so a plain click draws instead of aligning); once in progress it runs to
 * completion regardless of the modifier. The intercept hooks below give the
 * alignment first claim on pointer input and mute the subtool's draw pointer
 * while a gesture is active.
 */

import { SurfaceToolBase } from './SurfaceToolBase';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { PointoramaSurface } from '../../../utils/PointoramaSurface';
import { SubToolHosting } from '../../SubToolHosting';
import { SurfaceAlignHosting } from './SurfaceAlignHosting';
import { LineTool } from './LineTool';
import { SurfaceKindAdapter } from './SurfaceKindAdapter';
import { setMeasurementSphereScale } from './MeasureSubTool';

export class SurfaceHostTool extends SurfaceAlignHosting(SubToolHosting(SurfaceToolBase)) {
  readonly name = 'surface_line';

  private readonly surfaceAdapter: SurfaceKindAdapter;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);

    this.surfaceAdapter = new SurfaceKindAdapter();
    const lineTool = new LineTool(viewer, persona, undefined, this.surfaceAdapter);
    const ctx = this.surfaceAdapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(lineTool, this.surfaceAdapter, ctx);

    // Reload the segmentation pipeline when the surface kind changes.
    this.surfaceAdapter.setPipelineConfigChangedHandler(() => this.pushBasePlane());
  }

  // Lifecycle is otherwise owned by SubToolHosting; these overrides only add
  // the vertex-sphere sizing (full size in the tool, half size outside) —
  // surface annotations live in scene.measurements.

  protected onEnable(): void {
    super.onEnable();
    setMeasurementSphereScale(this.viewer, 1);
  }

  protected onDisable(): void {
    setMeasurementSphereScale(this.viewer, 0.5);
    super.onDisable();
  }

  protected onSurfaceChanged(_surface: PointoramaSurface | null, _identifier: string | null): void {
    this.surfaceAdapter.refreshForSelection();
    this.pushBasePlane();
  }

  protected onViewChanged(): void {
    this.pushBasePlane();
  }

  // Ctrl-gated camera alignment intercepts come from SurfaceAlignHosting.

  onClick(event: MouseEvent): void {
    if (this.isCameraAlignmentActive()) return;
    super.onClick(event);
  }
}
